import * as React from "react"
import { Box, Button, Checkbox, FormControl, FormControlLabel, ImageList, ImageListItem, InputLabel, MenuItem, Select, Tooltip, } from "@mui/material"
import { AXIOS_API_INSTANCE, get_service_order_color } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { LoadingBackdropIndeterminate } from "../LoadingBackdrop";
import { useSnackbar } from "notistack";
import { useTrackedUser } from "../UserState"
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from "@emotion/react"
import Chart from 'react-apexcharts'
import dayjs from "dayjs"
import { get_service_order_status_icon, sort_custom_statuses } from "../../Utils/service";

const button_sx = { height: '100%', display: 'block', padding: 4, fontSize: 16 }

export default function ServiceKPIsReport({ startDatetime, endDatetime, selectedLocations }) {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [my_user,] = useTrackedUser();
    const [isLoading, setIsLoading] = React.useState(false)
    const [kpiData, setKpiData] = React.useState([])
    const [selectedStep, setSelectedStep] = React.useState(7)
    const [excludeRepeatedStatuses, setExcludeRepeatedStatuses] = React.useState(true)
    const [series, setSeries] = React.useState({data: [], categories: [], tickAmount: 0, })
    const statuses = my_user.client.service_order_statuses
    const cols = [
        useMediaQuery(theme.breakpoints.only('xs')) && 2,
        useMediaQuery(theme.breakpoints.only('sm')) && 3,
        useMediaQuery(theme.breakpoints.only('md')) && 3,
        useMediaQuery(theme.breakpoints.only('lg')) && 3,
        useMediaQuery(theme.breakpoints.only('xl')) && 4
    ].filter((x) => x > 0)[0]

    const days = []
    for (let i = startDatetime; i <= endDatetime; i = i.add(1, 'day')) {
        days.push(i.startOf('day'))
    }

    const build_series = (kpiData, selectedStep) => {
        let new_series = {
            data: [],
            categories: days.filter((v, index) => (selectedStep === 1) || (index % selectedStep === 0)).map((day) => day.format('M/D/YYYY')),
            tickAmount: Math.ceil(days.length / selectedStep) - 1
        }
        sort_custom_statuses(statuses).forEach((status) => {
            Object.keys(kpiData).forEach((location_id) => {
                let data = []
                if (statuses[status].type === 'CREATED') {
                    data = days.map((day) => kpiData[location_id]?.[day.toISOString().slice(0, -5)]?.[`Created Service Order`] ?? null)
                } else {
                    data = days.map((day) => kpiData[location_id]?.[day.toISOString().slice(0, -5)]?.[`Changed Status To ${status}`] ?? null)
                }
                if (!data.every((x) => x === null)) {
                    if (selectedStep > 1) {
                        let new_data = []
                        for (let i = 0; i < data.length; i += selectedStep) {
                            const val = data.slice(i, Math.min(i + selectedStep, data.length)).reduce((partialSum, a) => partialSum + a ?? 0, 0)
                            new_data.push(val !== 0 ? val : null);
                        }
                        data = new_data
                    }
                    const color = get_service_order_color(my_user.client.service_order_statuses, status)
                    new_series.data.push({
                        name: `Changed Status To ${status} ${selectedLocations.find((loc) => loc.id.toString() === location_id)?.name}`,
                        group: `${selectedLocations.find((loc) => loc.id.toString() === location_id)?.name}`,
                        data: data,
                        color: color
                    })
                }
            })
        })
        setSeries(new_series)
    }

    const getKPIs = (start_datetime, end_datetime, location_ids, excludeRepeatedStatuses, selectedStep) => {
        if (!location_ids || location_ids.length === 0) {
            return
        }
        setIsLoading(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("reports/servicekpis", {
                params: {
                    start_datetime: start_datetime,
                    end_datetime: end_datetime.add(1, 'day'),
                    location_ids: location_ids,
                    exclude_repeats: excludeRepeatedStatuses
                },
            }).then((kpis) => {
                setKpiData(kpis.data.data)
                build_series(kpis.data.data, selectedStep)
                setIsLoading(false)
            }).catch((error) => {
                console.log(error)
                setIsLoading(false)
                enqueueSnackbar(`Failed to load data <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    React.useEffect(() => {
        let new_selectedStep = selectedStep
        if (dayjs(endDatetime).diff(dayjs.utc(startDatetime), 'days') > 15 && selectedStep === 1) {
            new_selectedStep = 7
        }
        getKPIs(startDatetime, endDatetime, selectedLocations.map((x) => x.id), excludeRepeatedStatuses, new_selectedStep)
        if (dayjs(endDatetime).diff(dayjs.utc(startDatetime), 'days') > 15 && selectedStep === 1) {
            setSelectedStep(7)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDatetime, endDatetime, selectedLocations, excludeRepeatedStatuses])

    React.useEffect(() => {
        if (!isLoading) {
            build_series(kpiData, selectedStep)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStep])

    return (
        <React.Fragment>
            <Box>
                <LoadingBackdropIndeterminate
                    open={isLoading}
                    text="Loading Service KPI Report"
                />
                {!isLoading && !series?.data &&
                    <h3>NO DATA</h3>
                }
                {!isLoading && series?.data &&
                    <Box>
                        <p>Service Order Status Changes over time. All events are associated with the original drop off location of a Service Order</p>
                        <FormControl>
                            <InputLabel id="kpis-group-time-category">Step</InputLabel>
                            <Select
                                labelId="kpis-group-time-category"
                                label="Step"
                                placeholder=''
                                onChange={(event) => {
                                    setSelectedStep(event.target.value)
                                }}
                                value={selectedStep}
                                sx={{ width: '115px' }}
                            >
                                {(days.length > 15 ? [7, 30, 60, 90] : [1, 7, 30, 60, 90]).map((n) =>
                                    <MenuItem key={`${n}`} value={n}
                                    >
                                        {`${n} Day${n > 1 ? 's' : ''}`}
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Tooltip
                            title="Exclude events where a service order entered a particular status in the past, and then was moved out of and back into that status"
                            arrow
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={excludeRepeatedStatuses}
                                        onChange={(event) => setExcludeRepeatedStatuses(event.target.checked)}
                                        disabled={isLoading}
                                    />
                                }
                                label="Exclude Repeat Events"
                            />
                        </Tooltip>
                        <Chart
                            type="bar" height={450}
                            options={{
                                chart: {
                                    height: 450,
                                    type: 'bar',
                                    stacked: true,
                                    width: '100%',
                                    zoom: {
                                        enabled: false
                                    },
                                    animations: {
                                        enabled: false
                                    }
                                },
                                plotOptions: {
                                    bar: {
                                        horizontal: false,
                                        columnWidth: '55%',
                                        borderRadius: 5,
                                        borderRadiusApplication: 'end',
                                    },
                                },
                                dataLabels: {
                                    enabled: false
                                },
                                stroke: {
                                    show: true,
                                    width: 2,
                                    curve: 'straight',
                                    colors: ['transparent']
                                },
                                xaxis: {
                                    // type: 'datetime',
                                    categories: series.categories,
                                    tickAmount: series.tickAmount,
                                    labels: {
                                        formatter: function (val, timestamp) {
                                            return dayjs.utc(val).format("MM/DD/YYYY");
                                        }
                                    }
                                },
                                yaxis: {
                                    decimalsInFloat: 1,
                                    title: {
                                        text: 'Number of Changes'
                                    }
                                },
                                fill: {
                                    opacity: 1
                                },
                                tooltip: {
                                    y: {
                                        formatter: function (val) {
                                            return val
                                        }
                                    }
                                }
                            }}
                            series={series.data}
                        />
                        <ImageList variant="standard" gap={2} cols={cols}>
                            {sort_custom_statuses(statuses).map((status) => {
                                let total_count = 0
                                Object.keys(kpiData).forEach((location) => {
                                    Object.keys(kpiData[location]).forEach((date) => {
                                        if (statuses[status].type === 'CREATED') {
                                            total_count += kpiData[location][date][`Created Service Order`] ?? 0
                                        } else {
                                            total_count += kpiData[location][date][`Changed Status To ${status}`] ?? 0
                                        }
                                    })
                                })
                                return (<ImageListItem key={status}>
                                    <Button sx={button_sx} color={get_service_order_color(my_user.client.service_order_statuses, status)} variant='contained' onClick={undefined}>
                                        <span>{get_service_order_status_icon(my_user.client.service_order_statuses[status].type)}</span>
                                        <br />
                                        <span>{total_count} Changed To {status}</span>
                                    </Button>
                                </ImageListItem>)
                            }
                            )
                            }
                        </ImageList>
                    </Box>
                }
            </Box>
        </React.Fragment>
    )
}
